import { useEffect } from "react";
import styled from "styled-components";

import { useCustomerPortal } from "../../context/CustomerPortalContext";
import CustomerHero from "../../components/customer/CustomerHero";
import CustomerCard from "../../components/customer/CustomerCard";
import CustomerInfoRow from "../../components/customer/CustomerInfoRow";
import CustomerNavigation from "../../components/customer/CustomerNavigation";

export default function CustomerDashboardPage() {
  const {
    currentCustomer,
    authLoading,
    error,
    success,
    fetchCustomer,
    logout,
  } = useCustomerPortal();

  useEffect(() => {
    if (!currentCustomer) {
      fetchCustomer();
    }
  }, [currentCustomer, fetchCustomer]);

  if (authLoading) {
    return (
      <PageWrapper>
        <PanelInfo>Chargement de votre espace client...</PanelInfo>
      </PageWrapper>
    );
  }

  if (error && !currentCustomer) {
    return (
      <PageWrapper>
        <PanelMessage $error>{error}</PanelMessage>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <CustomerPanel>
        <CustomerHero customer={currentCustomer} onLogout={logout} />

        <PortalLayout>
          <CustomerNavigation />

          <ContentArea>
            {success && <PanelMessage>{success}</PanelMessage>}

            <Grid>
              <CustomerCard title="Mon profil">
                <InfoList>
                  <CustomerInfoRow label="Prénom" value={currentCustomer?.firstname} />
                  <CustomerInfoRow label="Nom" value={currentCustomer?.lastname} />
                  <CustomerInfoRow label="E-mail" value={currentCustomer?.email} />
                  <CustomerInfoRow
                    label="Téléphone"
                    value={currentCustomer?.phone_number}
                  />
                  <CustomerInfoRow label="Ville" value={currentCustomer?.city} />
                  <CustomerInfoRow
                    label="UUID client"
                    value={currentCustomer?.uuid}
                    mono
                  />
                </InfoList>
              </CustomerCard>

              <CustomerCard title="Fidélité">
                <MetricBox>
                  <strong>{currentCustomer?.loyalty_points ?? 0}</strong>
                  <span>points disponibles</span>
                </MetricBox>
                <MutedText>
                  Les récompenses et avantages seront affichés ici dès que le
                  module fidélité sera activé.
                </MutedText>
              </CustomerCard>

              <CustomerCard title="Historique">
                <EmptyState>
                  Vos commandes, participations et réservations apparaîtront ici.
                </EmptyState>
              </CustomerCard>

              <CustomerCard title="Identifiants joueurs">
                <EmptyState>
                  Steam, PSN, Xbox, Nintendo, Discord et autres comptes de jeu
                  seront bientôt disponibles.
                </EmptyState>
              </CustomerCard>

              <CustomerCard title="Événements" full>
                <VaultGrid>
                  <VaultItem>
                    <strong>Tournois</strong>
                    <span>Inscriptions à venir</span>
                  </VaultItem>
                  <VaultItem>
                    <strong>Soirées jeux</strong>
                    <span>Programme à venir</span>
                  </VaultItem>
                  <VaultItem>
                    <strong>Réservations</strong>
                    <span>Historique à venir</span>
                  </VaultItem>
                </VaultGrid>
              </CustomerCard>
            </Grid>
          </ContentArea>
        </PortalLayout>
      </CustomerPanel>
    </PageWrapper>
  );
}

const PageWrapper = styled.main`
  min-height: 100dvh;
  width: 100vw;
  padding: 1.5rem;
  background: #0b1328;
  box-sizing: border-box;
  overflow-y: auto;
`;

const CustomerPanel = styled.section`
  max-width: 1280px;
  margin: 0 auto;
  color: #f7f7f7;
`;

const PortalLayout = styled.div`
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContentArea = styled.div`
  min-width: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const InfoList = styled.div`
  display: grid;
  gap: 0.7rem;
`;

const MetricBox = styled.div`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  text-align: center;
  margin-bottom: 1rem;

  strong {
    display: block;
    font-size: 2rem;
    font-weight: 950;
  }

  span {
    font-size: 0.82rem;
    opacity: 0.72;
  }
`;

const MutedText = styled.p`
  margin: 0;
  opacity: 0.72;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  opacity: 0.75;
`;

const VaultGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.85rem;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const VaultItem = styled.div`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;

  strong {
    display: block;
    font-weight: 950;
    margin-bottom: 0.35rem;
  }

  span {
    opacity: 0.68;
  }
`;

const PanelMessage = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;

const PanelInfo = styled.div`
  padding: 1rem;
  color: #f7f7f7;
  opacity: 0.75;
`;
